import { useEffect, useState } from 'react';
import { useTheme } from './ThemeProvider';

interface DynamicLogoProps {
  area: 'login' | 'header' | 'footer' | 'sidebar';
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
}

/**
 * Componente que carrega dinamicamente a logo apropriada baseado no tema e área
 */
export function DynamicLogo({ area, alt = 'Logo', className = '', fallback }: DynamicLogoProps) {
  const { theme, resolvedTheme } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Escutar evento de atualização de configurações
  useEffect(() => {
    const handleConfigUpdate = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('empresa-config-updated', handleConfigUpdate);
    return () => window.removeEventListener('empresa-config-updated', handleConfigUpdate);
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        // Determinar qual tema usar (light ou dark)
        const currentTheme = resolvedTheme || theme || 'dark';
        const isDark = currentTheme === 'dark';

        // Buscar configuração da empresa (com timestamp para evitar cache)
        const response = await fetch(`/api/theme?t=${Date.now()}`);
        if (!response.ok) {
          throw new Error('Falha ao carregar configuração');
        }

        const config = await response.json();

        // Selecionar a logo apropriada baseado na área e tema
        let selectedLogo: string | null = null;

        if (area === 'login' || area === 'header' || area === 'sidebar') {
          // Para login, header e sidebar, sempre usar logo escura (sidebar é sempre escura)
          selectedLogo = config.logoFolioEscuro;
        } else if (area === 'footer') {
          // Para footer, usar logo da empresa baseada no tema
          selectedLogo = isDark ? config.logoEmpresaEscuro : config.logoEmpresaClaro;
        }

        // Fallback para logos antigas se as novas não existirem
        if (!selectedLogo) {
          selectedLogo = config.logo || config.logoSecundario;
        }

        if (selectedLogo) {
          setLogoUrl(selectedLogo);
          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Erro ao carregar logo:', err);
        setError(true);
      }
    };

    fetchLogo();
  }, [area, theme, resolvedTheme, refreshKey]);

  // Se houver erro e um fallback foi fornecido, renderizar o fallback
  if (error && fallback) {
    return <>{fallback}</>;
  }

  // Se houver erro e não houver fallback, renderizar texto simples
  if (error) {
    return <div className={`font-bold text-brand ${className}`}>LOGO</div>;
  }

  // Se ainda estiver carregando
  if (!logoUrl) {
    return <div className={`animate-pulse bg-muted rounded ${className}`} style={{ minHeight: '40px', minWidth: '120px' }} />;
  }

  // Renderizar a imagem
  return (
    <img
      src={logoUrl}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
}
